import type {
  QueryTransitionsV3QueryVariables,
  TransitionsV3Filters,
} from '@revelio/data-access';

import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import { getNumericIdFromNestedId } from './get-numeric-id-from-nested-id';
import { getTransitionsV3BreakdownOptions } from './get-transitions-v3-breakdown-options';
import { getV3CompanyFilters } from './get-v3-company-filters';
import { getV3Entities } from './get-v3-entities';
import { getV3GeographyFilters } from './get-v3-geography-filters';
import { getV3ListFilter } from './get-v3-list-filter';
import { getV3Taxonomies } from './get-v3-taxonomies';
import { getDateFromFilters } from './getDateFilter';
import {
  appendRoleSelectionToFilters,
  appendSkillSelectionToV3Filters,
} from './role-skill-ent-kind';

const getTransitionsV3Filters = ({
  primaryDimension,
  filters,
}: {
  primaryDimension: PrimaryFilterId;
  filters: Filter[];
}): TransitionsV3Filters => {
  const transitionsFilters = filters.reduce<TransitionsV3Filters>(
    (acc, filter): TransitionsV3Filters => {
      if (filter.id === primaryDimension) {
        return acc;
      }
      switch (filter.id) {
        case FilterId.COMPANY:
          return { ...acc, ...getV3CompanyFilters(filter) };
        case FilterId.INDUSTRY:
          return { ...acc, ...getV3CompanyFilters(filter) };
        case FilterId.GEOGRAPHY:
          return { ...acc, ...getV3GeographyFilters(filter) };

        case FilterId.ROLE: {
          if (filter.type !== FilterType.TREE) return acc;
          return filter.selections.reduce<TransitionsV3Filters>(
            (inner, selection) => {
              const id = getNumericIdFromNestedId(String(selection.id));
              if (id === null) return inner;
              return {
                ...inner,
                ...appendRoleSelectionToFilters(
                  inner,
                  String(selection.levelId),
                  id
                ),
              };
            },
            acc
          );
        }

        case FilterId.CUSTOM_SKILL: {
          if (filter.type !== FilterType.TREE) return acc;
          return filter.selections.reduce<TransitionsV3Filters>(
            (inner, selection) => {
              const id = getNumericIdFromNestedId(String(selection.id));
              if (id === null) return inner;
              return {
                ...inner,
                ...appendSkillSelectionToV3Filters({
                  acc: inner,
                  levelId: String(selection.levelId),
                  id,
                }),
              };
            },
            acc
          );
        }

        case FilterId.GENDER:
          return { ...acc, ...getV3ListFilter({ filter, key: 'gender' }) };
        case FilterId.ETHNICITY:
          return { ...acc, ...getV3ListFilter({ filter, key: 'ethnicity' }) };
        case FilterId.EDUCATION:
          return {
            ...acc,
            ...getV3ListFilter({ filter, key: 'highest_degree' }),
          };
        case FilterId.SENIORITY:
          return { ...acc, ...getV3ListFilter({ filter, key: 'seniority' }) };

        case FilterId.TITLE: {
          if (filter.type !== FilterType.FREE_TEXT) return acc;
          return { ...acc, title: [filter.text] };
        }
        case FilterId.KEYWORDS: {
          if (filter.type !== FilterType.FREE_TEXT) return acc;
          return { ...acc, description: [filter.text] };
        }

        default:
          return acc;
      }
    },
    {}
  );

  const dateFilter = getDateFromFilters({
    filters,
    granularity: 'month',
  });

  if (dateFilter === null) {
    return transitionsFilters;
  }

  return {
    ...transitionsFilters,
    start_date: dateFilter.start_date,
    end_date: dateFilter.end_date,
  };
};

export const getTransitionsV3Variables = ({
  filters,
  primaryDimension,
}: {
  filters: Filter[];
  primaryDimension: PrimaryFilterId;
}): QueryTransitionsV3QueryVariables | null => {
  const entities = getV3Entities({ primaryDimension, filters });

  if (entities.length === 0) {
    return null;
  }

  const transitionsFilters = getTransitionsV3Filters({
    filters,
    primaryDimension,
  });

  const { role_taxonomy, skill_taxonomy } = getV3Taxonomies(filters);

  const breakdownOptions = getTransitionsV3BreakdownOptions({
    filters,
    transitionsFilters,
  });

  return {
    req: {
      entities,
      filters: transitionsFilters,
      role_taxonomy,
      skill_taxonomy,
      breakdown_options: breakdownOptions,
    },
  };
};
